/*!
 * Fresh Coast — Extracts stat count-up
 * Numbers animate 0 → target when they scroll into view, once, then
 * settle on the authored value. Runs alongside the .fc-reveal blur-fade-up
 * (fc-nav.js) so each stat lifts and defocuses while the number counts.
 *
 * No dependencies — IntersectionObserver + requestAnimationFrame.
 * Respects prefers-reduced-motion: the final value is shown instantly,
 * with no counting. Suffixes, prefixes and thousands separators are
 * preserved (e.g. "800+", "140,000+", "30,000,000+", "5,000,000").
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DURATION = 1800;

  /** Insert en-US thousands separators into an integer string. */
  function groupThousands(intStr) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Split an authored stat into prefix / numeric target / suffix.
   * Preserves whether the source used comma grouping and how many
   * decimal places it carried, so the animated value re-formats to match.
   */
  function parseTarget(text) {
    var match = text.match(/[\d.,]+/);
    if (!match) return null;

    var raw = match[0];
    var prefix = text.slice(0, match.index);
    var suffix = text.slice(match.index + raw.length);
    var hasComma = raw.indexOf(',') !== -1;

    // Treat a single trailing dot group as decimals only when it is not
    // being used as a thousands separator (our data is integer, but be safe).
    var decimals = 0;
    var dotParts = raw.split('.');
    if (!hasComma && dotParts.length === 2 && dotParts[1].length > 0 && dotParts[1].length <= 3) {
      decimals = dotParts[1].length;
    }

    var value = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(value)) return null;

    return { prefix: prefix, suffix: suffix, target: value, hasComma: hasComma, decimals: decimals };
  }

  function formatValue(value, cfg) {
    var out = cfg.decimals > 0 ? value.toFixed(cfg.decimals) : String(Math.round(value));
    if (cfg.hasComma) {
      var parts = out.split('.');
      parts[0] = groupThousands(parts[0]);
      out = parts.join('.');
    }
    return cfg.prefix + out + cfg.suffix;
  }

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function animate(el, cfg) {
    var start = null;
    function frame(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / DURATION, 1);
      el.textContent = formatValue(cfg.target * easeOutExpo(p), cfg);
      if (p < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = formatValue(cfg.target, cfg);
      }
    }
    requestAnimationFrame(frame);
  }

  function initCountUps() {
    var els = document.querySelectorAll('[data-fc-countup]');
    if (!els.length) return;

    Array.prototype.forEach.call(els, function (el) {
      if (el.getAttribute('data-fc-countup-init') === 'true') return;

      var cfg = parseTarget((el.textContent || '').trim());

      // No parseable number, or reduced motion → leave the final value as authored.
      if (!cfg || REDUCED) {
        el.setAttribute('data-fc-countup-init', 'true');
        return;
      }

      el.setAttribute('data-fc-countup-init', 'true');
      // Announce the final figure to assistive tech; the visual node counts.
      el.setAttribute('aria-label', formatValue(cfg.target, cfg));
      el.textContent = formatValue(0, cfg);

      if (typeof IntersectionObserver === 'undefined') {
        animate(el, cfg);
        return;
      }

      var io = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            animate(el, cfg);
          });
        },
        { threshold: 0.35, rootMargin: '0px 0px -8% 0px' }
      );

      io.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCountUps);
  } else {
    initCountUps();
  }

  // Re-scan when Shopify swaps section HTML in the theme editor.
  document.addEventListener('shopify:section:load', initCountUps);
})();
